import { useEffect, useState } from "react";
import "./index.scss";
import { useDispatch } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import store from "store";
import { Actions as ApiCallActions } from "../../../redux/apiCall/reducers";
import { Actions as AuthActions } from "../../../redux/auth/reducers";
import KvkkModal from "../../../components/kvkk-modal";
import general from "../../../utils/general";
import LanguageSwitcher from "../../../components/language-switcher";
import useI18n from "../../../i18n/useI18n";
import RegisterFormContent from "./components/RegisterFormContent";
import VerificationContent from "./components/VerificationContent";
import RegisterProfileComplete from "./components/RegisterProfileComplete";
import RegisterWithAgent from "./RegisterWithAgent";

const assetStylesheets = [
  "/assets/plugins/bootstrap/css/bootstrap.min.css",
  "/assets/css/main.css",
  "/assets/css/authentication.css",
  "/assets/css/color_skins.css",
];

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useI18n();
  const [navOpen, setNavOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
    userType: "student", // Default olarak öğrenci seçili
    agentId: "",
    kvkkAccepted: false,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [showKvkkError, setShowKvkkError] = useState(false);
  const [isKvkkModalOpen, setIsKvkkModalOpen] = useState(false);
  const [isUserTypeLocked, setIsUserTypeLocked] = useState(false);
  const [isVerificationStep, setIsVerificationStep] = useState(false);
  const [isDocumentUploadStep, setIsDocumentUploadStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isAgentFlow, setIsAgentFlow] = useState(false);

  const computeFormValidity = (errors, kvkkAccepted) =>
    Object.values(errors).every((error) => error === "") && kvkkAccepted;

  // CSS'ler route helper tarafından yönetiliyor, burada sadece yüklendiğinden emin oluyoruz
  useEffect(() => {
    assetStylesheets.forEach((href) => {
      const existing = document.querySelector(`link[href="${href}"]`);
      if (!existing) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
      }
    });
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (navOpen) {
      html.classList.add("nav-open");
    } else {
      html.classList.remove("nav-open");
    }
    return () => html.classList.remove("nav-open");
  }, [navOpen]);

  // URL'den gelen agent ve usertype parametrelerini oku; varsa formu kilitle
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlUserType = searchParams.get("usertype");
    const urlAgentId = searchParams.get("agent");

    if (urlUserType === "student" && urlAgentId) {
      setFormData((prev) => ({
        ...prev,
        userType: "student",
        agentId: urlAgentId,
      }));
      setIsUserTypeLocked(true);
      setIsAgentFlow(true);
    }
  }, [location.search]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (name === "kvkkAccepted") {
      setShowKvkkError(!newValue);
      setIsFormValid(computeFormValidity(validationErrors, newValue));
    }
  };

  const handleUserTypeChange = (userType) => {
    if (isUserTypeLocked) return;
    setFormData((prev) => ({
      ...prev,
      userType: userType,
    }));
  };

  const handleValidationChange = (fieldName, isValid, errorMessage) => {
    const updatedErrors = {
      ...validationErrors,
      [fieldName]: isValid ? "" : errorMessage,
    };

    setValidationErrors(updatedErrors);
    setIsFormValid(computeFormValidity(updatedErrors, formData.kvkkAccepted));
  };

  const handleVerificationValidationChange = (fieldName, isValid, errorMessage) => {
    if (fieldName !== "verificationCode") return;
    setVerificationCodeError(isValid ? "" : errorMessage);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.kvkkAccepted) {
      setShowKvkkError(true);
      general.notificationError(t("AUTH_REGISTER_KVKK_ERROR"));
      return;
    }

    // Form validation kontrolü
    if (!isFormValid) {
      general.notificationError(t("AUTH_REGISTER_FIX_ERRORS"));
      return;
    }

    dispatch(
      ApiCallActions.Post({
        url: "auth/register",
        data: formData,
        query: { lang: i18n.language },
        showAlertOnError: true,
        showLoading: true,
        onSuccess: async ({ data }) => {
          // Başarılı kayıt mesajını göster ve doğrulama adımına geç
          general.messageSuccess(t("AUTH_REGISTER_SUCCESS"));
          general.messageSuccess("E-posta doğrulama kodu gönderildi. Lütfen e-postanızı kontrol edin.");
          setPendingEmail(formData.email);
          setIsVerificationStep(true);
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage)
          // Hata mesajı showAlertOnError ile otomatik gösteriliyor
          // Burada ek işlemler yapılabilir
        },
      })
    );
  };

  const handleResendVerification = () => {
    // e.preventDefault() handled in child component
    const emailToResend = pendingEmail || formData.email;
    if (!emailToResend) {
      general.notificationError("E-posta adresi bulunamadı.");
      return;
    }

    dispatch(
      ApiCallActions.Post({
        url: "auth/resend-verification",
        data: { email: emailToResend },
        query: { lang: i18n.language },
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          general.messageSuccess("Doğrulama kodu tekrar gönderildi.");
        },
        onError: ({ errorMessage }) => {
          if (errorMessage) {
            general.notificationError(errorMessage);
          }
        },
      })
    );
  };

  const handleVerifyEmail = (event) => {
    event.preventDefault();
    const emailToVerify = pendingEmail || formData.email;
    const codeToVerify = verificationCode.trim();

    if (!emailToVerify) {
      general.notificationError("E-posta bulunamadı. Lütfen tekrar kayıt olun.");
      return;
    }

    if (!codeToVerify) {
      general.notificationError("Lütfen e-posta ile gelen kodu giriniz.");
      return;
    }

    if (verificationCodeError) {
      general.notificationError(verificationCodeError);
      return;
    }

    setIsVerifying(true);
    dispatch(
      ApiCallActions.Post({
        url: "auth/verify-email",
        data: { email: emailToVerify, code: codeToVerify },
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          general.messageSuccess("E-posta doğrulandı.");

          // Eğer agent ile geldiyse otomatik login yap
          if (formData.agentId) {
            handleAutoLogin();
          } else {
            general.messageSuccess("Giriş yapabilirsiniz.");
            setTimeout(() => navigate("/auth/login"), 1200);
            setIsVerifying(false); // Sadece normal akışta waiting'i bitir (diğerinde login devam edecek)
          }
        },
        onError: ({ errorMessage }) => {
          if (errorMessage) {
            general.notificationError(errorMessage);
          }
          setIsVerifying(false);
        },
        // callback: () => setIsVerifying(false), // Manuel yönetiyoruz
      })
    );
  };


  const handleAutoLogin = () => {
    const loginData = {
      email: formData.email,
      password: formData.password
    };

    dispatch(
      ApiCallActions.Post({
        url: "auth/login",
        data: loginData,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: async ({ data }) => {
          const tokens = data?.data?.tokens || data?.tokens;

          if (tokens && tokens.accessToken) {
            store.set("token", tokens.accessToken);
            if (tokens.refreshToken) {
              store.set("refreshToken", tokens.refreshToken);
            }
            if (tokens.expiresAt) {
              store.set("tokenExpiresAt", tokens.expiresAt);
            }

            // Profil bilgilerini çek
            dispatch(
              ApiCallActions.Get({
                url: 'auth/profile',
                showAlertOnError: false,
                showLoading: false,
                onSuccess: ({ data }) => {
                  const profileData = data?.data || data;
                  store.set("userInfo", profileData);
                  dispatch(AuthActions.setAuthState({
                    userInfo: profileData,
                    isUserAuthorized: true
                  }));

                  // Login başarılı, belge yükleme adımına geç
                  setIsVerifying(false);
                  setIsVerificationStep(false);
                  setIsDocumentUploadStep(true);
                },
                onError: () => {
                  console.error('Profile fetch error');
                  navigate("/auth/login"); // Profil çekilemezse login'e at
                }
              })
            );
          } else {
            navigate("/auth/login");
          }
        },
        onError: () => {
          setIsVerifying(false);
          navigate("/auth/login");
        }
      })
    );
  }

  // If in Agent Flow, use the dedicated component
  if (isAgentFlow) {
    return <RegisterWithAgent />;
  }

  if (isDocumentUploadStep) {
    return (
      <div className="agent-onboarding-wrapper">
        <div className="onboarding-overlay"></div>

        <nav className="navbar navbar-expand-lg fixed-top navbar-transparent onboarding-navbar">
          <div className="container-fluid" style={{ maxWidth: '100%', width: '100%', paddingLeft: '30px', paddingRight: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <a className="navbar-brand" href="/" style={{ padding: 0 }}>
              <img
                src="https://www.yosdanismanim.com/wp-content/uploads/2025/11/yos-logo.png"
                alt="YÖS Portal"
                style={{ height: '50px', filter: 'brightness(0) invert(1)' }}
              />
            </a>

            <ul className="navbar-nav navbar-flex-row" style={{ flexDirection: 'row', gap: '15px', alignItems: 'center', margin: 0 }}>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_TWITTER_TITLE")}
                  href="https://x.com/yosdanismanim"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#fff' }}
                >
                  <i className="zmdi zmdi-twitter"></i>
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_YOUTUBE_TITLE")}
                  href="https://www.youtube.com/@yosdanismanim"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#fff' }}
                >
                  <i className="zmdi zmdi-youtube"></i>
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_INSTAGRAM_TITLE")}
                  href="https://www.instagram.com/yosdanismanim/?igsh=MTZ4cTB3ejBwcDVqbg%3D%3D#"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#fff' }}
                >
                  <i className="zmdi zmdi-instagram"></i>
                </a>
              </li>
              <li className="nav-item" >
                <div>
                  <LanguageSwitcher />
                </div>
              </li>
            </ul>
          </div>
        </nav>

        <div className="container onboarding-container">
          <RegisterProfileComplete />
        </div>
      </div>
    );
  }

  return (
    <section className="theme-blush authentication sidebar-collapse">
      <nav className="navbar navbar-expand-lg fixed-top navbar-transparent">
        <div className="container" style={{ justifyContent: 'flex-end', marginRight: 15 }}>

          <div>
            <ul className="navbar-nav navbar-flex-row">
              <li className="nav-item">
                <a className="nav-link" href="https://www.yosdanismanim.com/" target="_blank" rel="noreferrer">
                  {t("COMMON_HOME")}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_TWITTER_TITLE")}
                  href="https://x.com/yosdanismanim"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="zmdi zmdi-twitter"></i>
                  {/* <p className="d-lg-none d-xl-none">{t("COMMON_SOCIAL_TWITTER")}</p> */}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_YOUTUBE_TITLE")}
                  href="https://www.youtube.com/@yosdanismanim"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="zmdi zmdi-youtube"></i>
                  {/* <p className="d-lg-none d-xl-none">{t("COMMON_SOCIAL_YOUTUBE")}</p> */}
                </a>
              </li>
              <li className="nav-item">
                <a
                  className="nav-link"
                  title={t("AUTH_NAV_INSTAGRAM_TITLE")}
                  href="https://www.instagram.com/yosdanismanim/?igsh=MTZ4cTB3ejBwcDVqbg%3D%3D#"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="zmdi zmdi-instagram"></i>
                  {/* <p className="d-lg-none d-xl-none">{t("COMMON_SOCIAL_INSTAGRAM")}</p> */}
                </a>
              </li>
              <li className="nav-item" >
                <div className="navbar-lang-switcher" style={{ marginTop: 13, marginLeft: 5 }}>
                  <LanguageSwitcher />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div className="page-header">
        <div className="page-header-image register-page-header-image"></div>
        <div className="container">
          <div className="col-md-12 content-center">
            <div className="card-plain">
              <form className="form" onSubmit={isVerificationStep ? handleVerifyEmail : handleSubmit}>
                <div className="header">
                  <div className="logo-container">
                    <img src="https://www.yosdanismanim.com/wp-content/uploads/2025/11/yos-logo.png" alt="YÖS Portal" />
                  </div>
                  <h5>
                    {isVerificationStep
                      ? "E-posta Doğrulama"
                      : t("AUTH_REGISTER_TITLE")}
                  </h5>
                </div>

                <div className="content">
                  {isDocumentUploadStep ? (
                    <RegisterProfileComplete />
                  ) : isVerificationStep ? (
                    <VerificationContent
                      pendingEmail={pendingEmail}
                      fallbackEmail={formData.email}
                      verificationCode={verificationCode}
                      setVerificationCode={setVerificationCode}
                      handleVerificationValidationChange={handleVerificationValidationChange}
                      onResendCode={handleResendVerification}
                    />
                  ) : (
                    <RegisterFormContent
                      formData={formData}
                      handleChange={handleChange}
                      handleUserTypeChange={handleUserTypeChange}
                      isUserTypeLocked={isUserTypeLocked}
                      handleValidationChange={handleValidationChange}
                      t={t}
                      showKvkkError={showKvkkError}
                      onKvkkLinkClick={() => setIsKvkkModalOpen(true)}
                    />
                  )}
                </div>

                <div className="footer text-center">
                  {isVerificationStep ? (
                    <>
                      <button
                        type="submit"
                        className="btn btn-warning btn-round btn-lg btn-block"
                        disabled={isVerifying}
                      >
                        {isVerifying ? "Doğrulanıyor..." : "Kodu Doğrula"}
                      </button>
                      <h5>
                        <Link to="/auth/login" className="link">
                          {t("AUTH_REGISTER_LOGIN_CTA")}
                        </Link>
                      </h5>
                    </>
                  ) : (
                    <>
                      <button
                        type="submit"
                        className="btn btn-warning btn-round btn-lg btn-block"
                      >
                        {t("AUTH_REGISTER_SUBMIT")}
                      </button>
                      <h5>
                        <Link to="/auth/login" className="link">
                          {t("AUTH_REGISTER_HAVE_ACCOUNT")}{" "}
                          <span className="text-warning">{t("AUTH_REGISTER_LOGIN_CTA")}</span>

                        </Link>
                      </h5>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
        <KvkkModal isOpen={isKvkkModalOpen} onClose={() => setIsKvkkModalOpen(false)} />
        <footer className="footer">
          <div className="container">
            <nav>
              <ul>
                <li>
                  <a href="http://thememakker.com/contact/" target="_blank" rel="noreferrer">
                    {t("AUTH_FOOTER_CONTACT")}
                  </a>
                </li>
                <li>
                  <a href="http://thememakker.com/about/" target="_blank" rel="noreferrer">
                    {t("AUTH_FOOTER_ABOUT")}
                  </a>
                </li>
                <li>
                  <a href="javascript:void(0);">{t("AUTH_FOOTER_FAQ")}</a>
                </li>
              </ul>
            </nav>
            <div className="copyright">
              &copy; {new Date().getFullYear()},{" "}
              <span>
                <a className="text-warning" href="http://algoraft.com/" target="_blank" rel="noreferrer">
                  Algoraft.com
                </a>
                &nbsp;{t("AUTH_FOOTER_DESIGNED_BY")}{" "}
              </span>
            </div>
          </div>
        </footer>

      </div>
    </section>
  );
};

export default Register;
